import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";

const HEADERS = ["ID", "ESTADO", "PRIORIDAD", "TITULO", "DESCRIPCION", "NOTA_HUMANA", "RESULTADO_BOT", "INTENTOS", "ORIGEN", "ULTIMA_EJECUCION", "GUIA_REVISION_HUMANA"];

const EMPTY_FORM = {
  enabled: true,
  spreadsheetId: "",
  sheetName: "Backlog",
  serviceAccountEmail: "",
  privateKey: "",
  idPrefix: "MEJ",
  origin: "JARVIS",
  reviewEnabled: true,
};

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function statusTone(status) {
  if (status === "SOLUCIONADO") return "success";
  if (status === "HECHO_POR_BOT") return "warning";
  return "default";
}

export default function OwnerJarvisLabPage() {
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [config, setConfig] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [error, setError] = React.useState("");

  const patch = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [configResponse, itemsResponse] = await Promise.all([
        ownerApi.jarvisBacklogConfig(),
        ownerApi.listJarvisBacklog({ limit: 80 }),
      ]);
      const next = configResponse?.config || null;
      setConfig(next);
      setForm({
        ...EMPTY_FORM,
        ...(next || {}),
        privateKey: "",
      });
      setItems(itemsResponse?.items || []);
    } catch (err) {
      setError(formatApiError(err, "No pude leer Jarvis Lab."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const run = async (key, work, success) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      const response = await work();
      setNotice(typeof success === "function" ? success(response) : success);
      await load();
      return response;
    } catch (err) {
      setError(formatApiError(err, "No pude completar la operación."));
      return null;
    } finally {
      setBusy("");
    }
  };

  const save = () => run("save", () => ownerApi.saveJarvisBacklogConfig({
    enabled: form.enabled,
    spreadsheetId: form.spreadsheetId,
    sheetName: form.sheetName,
    serviceAccountEmail: form.serviceAccountEmail,
    ...(form.privateKey.trim() ? { privateKey: form.privateKey } : {}),
    idPrefix: form.idPrefix,
    origin: form.origin,
    reviewEnabled: form.reviewEnabled,
  }), "Conexión guardada. Ahora podés probarla.");

  const test = () => run("test", () => ownerApi.testJarvisBacklog(), (response) => response?.empty
    ? "Conexión correcta. La pestaña está vacía: podés preparar el encabezado."
    : "Conexión y formato de columnas correctos.");

  const prepare = () => run("prepare", () => ownerApi.prepareJarvisBacklog(), "Encabezado verificado/preparado sin tocar filas existentes.");
  const sync = () => run("sync", () => ownerApi.syncJarvisBacklog(), "Backlog sincronizado. Los fixes locales pendientes fueron enviados.");

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar Google Sheets? Los fixes locales no se borran.")) return;
    await run("delete", () => ownerApi.deleteJarvisBacklogConfig(), "Google Sheets quedó desconectado. El backlog local se conserva.");
  };

  return (
    <Stack data-owner-jarvis-section="jarvis-lab" spacing={1.2} sx={{ width: "100%", pb: 4 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
        <Box>
          <Stack direction="row" spacing={.8} alignItems="center">
            <BugReportRoundedIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 950 }}>Jarvis Lab</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">Jarvis anota sus propios errores/mejoras, conserva contexto y los tramita por tu backlog.</Typography>
        </Box>
        <Stack direction="row" spacing={.7} alignItems="center">
          <Chip size="small" label={`${items.length} locales`} />
          <Chip size="small" variant="outlined" color={config?.enabled ? "success" : "default"} label={config?.enabled ? "Sheets activo" : "Sheets sin conectar"} />
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}

      <Paper variant="outlined" sx={{ p: { xs: 1.2, md: 1.6 }, borderRadius: 3 }}>
        <Stack spacing={1.1}>
          <Box>
            <Typography sx={{ fontWeight: 900 }}>Conexión Google Sheets</Typography>
            <Typography variant="body2" color="text.secondary">
              Habilitá Google Sheets API, creá una Service Account y copiá <b>client_email</b> + <b>private_key</b> de su JSON. Compartí la planilla con ese email como <b>Editor</b>. Jarvis no necesita tu contraseña de Google.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <TextField fullWidth size="small" label="URL o ID de la planilla" value={form.spreadsheetId} onChange={(e) => patch("spreadsheetId", e.target.value)} />
            <TextField size="small" label="Pestaña" value={form.sheetName} onChange={(e) => patch("sheetName", e.target.value)} sx={{ minWidth: { md: 190 } }} />
          </Stack>
          <TextField fullWidth size="small" label="Email de Service Account" value={form.serviceAccountEmail} onChange={(e) => patch("serviceAccountEmail", e.target.value)} placeholder="jarvis@proyecto.iam.gserviceaccount.com" />
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={7}
            size="small"
            label={config?.hasPrivateKey ? "Private key (dejar vacío para conservar la actual)" : "Private key"}
            value={form.privateKey}
            onChange={(e) => patch("privateKey", e.target.value)}
            placeholder="-----BEGIN PRIVATE KEY-----"
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField size="small" label="Prefijo ID" value={form.idPrefix} onChange={(e) => patch("idPrefix", e.target.value)} sx={{ width: { sm: 150 } }} />
            <TextField size="small" label="Origen" value={form.origin} onChange={(e) => patch("origin", e.target.value)} sx={{ width: { sm: 180 } }} />
            <FormControlLabel control={<Switch checked={Boolean(form.enabled)} onChange={(e) => patch("enabled", e.target.checked)} />} label="Sincronizar" />
            <FormControlLabel control={<Switch checked={Boolean(form.reviewEnabled)} onChange={(e) => patch("reviewEnabled", e.target.checked)} />} label="Preguntar revisión" />
          </Stack>

          <Stack direction="row" spacing={.7} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={save} disabled={Boolean(busy)}>Guardar</Button>
            <Button startIcon={<ScienceRoundedIcon />} onClick={test} disabled={Boolean(busy) || !config}>Probar</Button>
            <Button onClick={prepare} disabled={Boolean(busy) || !config}>Preparar encabezado</Button>
            <Button startIcon={<CloudSyncRoundedIcon />} onClick={sync} disabled={Boolean(busy) || !config}>Sincronizar</Button>
            {config ? <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={disconnect} disabled={Boolean(busy)}>Desconectar</Button> : null}
            {busy ? <CircularProgress size={20} sx={{ alignSelf: "center" }} /> : null}
          </Stack>

          {config?.lastError ? <Alert severity="warning">Último error: {config.lastError}</Alert> : null}
          <Typography variant="caption" color="text.secondary">Última sincronización: {dateLabel(config?.lastSyncedAt)} · Última prueba: {dateLabel(config?.lastTestedAt)}</Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 1.2, md: 1.6 }, borderRadius: 3 }}>
        <Typography sx={{ fontWeight: 900, mb: .5 }}>Contrato de la hoja</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Jarvis valida estas primeras 11 columnas antes de escribir. Si no coinciden, frena en vez de acomodar tu Sheet a la fuerza.
        </Typography>
        <Stack direction="row" spacing={.55} flexWrap="wrap" useFlexGap>
          {HEADERS.map((header) => <Chip key={header} size="small" variant="outlined" label={header} />)}
        </Stack>
        <Divider sx={{ my: 1.2 }} />
        <Typography variant="body2"><b>Dictado:</b> “Jarvis, anotá un error: en cobros no aparece la próxima fecha”.</Typography>
        <Typography variant="body2"><b>Seguimiento:</b> “MEJ-126 sigue mal porque el monto se duplica”.</Typography>
        <Typography variant="body2"><b>Ciclo:</b> HECHO_POR_BOT → Jarvis pregunta cómo quedó → SOLUCIONADO o vuelve a ACTIVO con tu comentario.</Typography>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 1.3 }}>
          <Typography sx={{ fontWeight: 900 }}>Backlog reciente</Typography>
          <Typography variant="body2" color="text.secondary">La copia local evita perder un reporte si Google está caído o todavía no configuraste la conexión.</Typography>
        </Box>
        <Divider />
        {loading ? <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={24} /></Box> : items.length ? (
          <Stack divider={<Divider flexItem />}>
            {items.slice(0, 80).map((item) => (
              <Box key={item._id || item.externalId} sx={{ p: 1.2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={.7} alignItems={{ sm: "center" }}>
                  <Stack direction="row" spacing={.55} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={item.externalId || "LOCAL"} />
                    <Chip size="small" color={statusTone(item.status)} variant={item.status === "ACTIVO" ? "outlined" : "filled"} label={item.status || "ACTIVO"} />
                    <Chip size="small" variant="outlined" label={item.priority || "MEDIA"} />
                    {item?.sync?.state !== "synced" ? <Chip size="small" color="warning" variant="outlined" label="pendiente sync" /> : null}
                  </Stack>
                  <Typography sx={{ fontWeight: 800, flex: 1 }}>{item.title}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {[item?.sourceContext?.sectionLabel, item?.sourceContext?.entityLabel, item?.sourceContext?.route].filter(Boolean).join(" · ") || "Sin contexto de vista"}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : <Typography sx={{ p: 2 }} color="text.secondary">Todavía no hay fixes capturados.</Typography>}
      </Paper>
    </Stack>
  );
}
